import { useState } from 'react'
import {
  postHelpPage,
  getHelpPageList,
  getPageList,
  postCheckPageExist,
  getDetailHelpPage,
  putUpdateHelpPage,
  getHelpPageHistory,
  putRestoreHelpPage,
  getCurrentDetailHelpPage,
} from '../../shared/services/HelpPageService'

interface Export {
  chosenColumnNameList: string[]
  pageName: string
  header: string
}

export interface IReqHelpPagePayload {
  searchTerm?: string
  searchColumns?: Array<string>
  isDesc?: boolean
  sortColumns?: Array<string>
  pageSize?: number
  pageIndex?: number
  filterOption?: number
  export?: Export
}

export interface IReqPageListPayload extends IReqHelpPagePayload {}

export interface IReqCreateHelpPage {
  mainPageId: string
  mainPageName: string
  url: string
  content: string
  status: number
  publishSchedule: string | null
}

export interface IReqUpdateHelpPage extends IReqCreateHelpPage {
  parentId: string
}

export interface IReqCheckPageExist {
  idEdit: string | null
  mainPageId: string
  mainPageName: string
  url: string
}

interface IHelpPage {
  id: string
  mainPageId: string
  mainPageName: string
  url: string
  content: string
  status: number
  publishSchedule: string | null
  version: number
  createdOn: string
  updatedOn: string
  createdBy: string
  createdByName: string
  updatedBy: string
  updatedByName: string
  currentVersionId: string
}

export interface IHelpPageDetail extends IHelpPage {
  parentId: string
  submoduleCodeName: string
  isCurrent: boolean
}

interface IResPage {
  id: string
  submoduleCodeName: string
  submoduleName: string
  moduleName: string
  pageUrl: string
  dateLastUpdated: string
  userLastUpdated: string
}

export interface IReqRestoreHelpPage {
  id: string
  publishSchedule: string | null
  status: number
}

export interface IVersionHistory {
  createdBy: string
  createdOn: string
  id: string
  isCurrent: boolean
  parentId: string
  publishSchedule: string | null
  updatedBy: string
  updatedOn: string
  version: number
}

export const useHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [helpPageList, setHelpPageList] = useState<Array<IHelpPage>>([])
  const [totalRecords, setTotalRecords] = useState(0)

  const formatHelpPageData = (data: any) => {
    return data?.map((item: any) => ({
      ...item,
    }))
  }

  const getHelpPageListData = async (params: IReqHelpPagePayload) => {
    try {
      setIsLoading(true)
      const data = await getHelpPageList(params)
      setHelpPageList(formatHelpPageData(data?.data?.items))
      setTotalRecords(data?.data?.total)
      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { helpPageList, totalRecords, getHelpPageListData, isLoading }
}

export const usePageSetup = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [pageNameOptions, setPageNameOptions] = useState<Array<{ label: string; value: string; id: string }>>([])
  const [totalRecords, setTotalRecords] = useState(0)

  const formatResData = (data: IResPage[]): { label: string; value: string; id: string }[] => {
    return data?.map((item: IResPage) => ({
      label: item.submoduleName,
      value: item.pageUrl,
      id: item.id,
    }))
  }

  const getPageNameOptions = async () => {
    try {
      setIsLoading(true)

      const data = await getPageList({
        pageSize: 100,
      })
      setPageNameOptions(formatResData(data?.data?.items))
      setTotalRecords(data?.data?.total)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { pageNameOptions, totalRecords, getPageNameOptions, isLoading }
}

export const useCreateHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)

  const createHelpPage = async (payload: IReqCreateHelpPage) => {
    try {
      setIsLoading(true)
      const data = await postHelpPage(payload)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, createHelpPage }
}

export const useCheckPageExist = () => {
  const [isLoading, setIsLoading] = useState(false)

  const checkPageExist = async (payload: IReqCheckPageExist): Promise<{ isExist: boolean }> => {
    try {
      setIsLoading(true)
      const data = await postCheckPageExist(payload)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
      return { isExist: true }
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, checkPageExist }
}

export const useDetailHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [helpPageDetail, setHelpPageDetail] = useState<IHelpPageDetail>()

  const getHelpPage = async (id: string) => {
    try {
      setIsLoading(true)
      const data = await getDetailHelpPage(id)

      setHelpPageDetail(data?.data)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, helpPageDetail, getHelpPage }
}

export const useUpdateHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)

  const updateHelpPage = async (id: string, payload: IReqUpdateHelpPage) => {
    try {
      setIsLoading(true)
      const data = await putUpdateHelpPage(id, payload)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, updateHelpPage }
}

export const useVersionStory = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [versionStoryList, setVersionStoryList] = useState<Array<IVersionHistory>>([])
  const [totalRecords, setTotalRecords] = useState(0)

  const formatVersionStoryData = (data: any) => {
    return data?.map((item: any) => ({
      ...item,
    }))
  }

  const getVersionStoryListData = async (parentId: string, payload: IReqHelpPagePayload) => {
    try {
      setIsLoading(true)
      const data = await getHelpPageHistory(parentId, payload)

      setVersionStoryList(formatVersionStoryData(data?.data.data))
      setTotalRecords(data?.data?.total)
      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { versionStoryList, totalRecords, getVersionStoryListData, isLoading }
}

export const useRestoreHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)

  const restoreHelpPage = async (payload: IReqRestoreHelpPage) => {
    try {
      setIsLoading(true)
      const data = await putRestoreHelpPage(payload)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, restoreHelpPage }
}

export const useCurrentDetailHelpPage = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [helpPageDetail, setHelpPageDetail] = useState<IHelpPageDetail>()

  const getHelpPage = async (id: string) => {
    try {
      setIsLoading(true)
      const data = await getCurrentDetailHelpPage(id)

      setHelpPageDetail(data?.data)

      return data?.data
    } catch (e) {
      console.log('ERROR: ', e)
    } finally {
      setIsLoading(false)
    }
  }

  return { isLoading, helpPageDetail, getHelpPage }
}
